/**
 * Editable WhatsApp message: shows the recipient's number (when usable), lets the admin adjust the
 * text, then opens wa.me with the chat and message ready. WhatsApp links cannot attach files, so the
 * message carries a link instead.
 */

import { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { bolivianWhatsAppNumber, whatsappShareUrl } from '@/features/events/utils/whatsapp';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  recipientName?: string;
  /** Raw phone as stored; when missing or invalid the admin picks the chat in WhatsApp */
  phone?: string | null;
  defaultMessage: string;
}

export function WhatsAppMessageDialog({ open, onOpenChange, title = 'Enviar por WhatsApp', recipientName, phone, defaultMessage }: Props) {
  const [message, setMessage] = useState(defaultMessage);
  useEffect(() => {
    if (open) setMessage(defaultMessage);
  }, [open, defaultMessage]);

  const number = bolivianWhatsAppNumber(phone);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {number
              ? `Se abrirá el chat con ${recipientName ?? 'el participante'} (+${number}).`
              : 'No hay un celular válido registrado: elija el contacto en WhatsApp.'}
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!message.trim()}
            onClick={() => {
              window.open(whatsappShareUrl(phone, message), '_blank', 'noopener,noreferrer');
              onOpenChange(false);
            }}
          >
            <MessageCircle className="mr-2 size-4" /> Abrir WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
